import { useQuery } from '@tanstack/react-query'
import epsClient from 'enrollment-platform/api/eps.client'

interface GlobalFeatureFlagResponse {
  key: string
  isEnabled: boolean
}

/**
 * Reads a global feature flag from the EPS platform-configurations store.
 *
 * This is the non-LaunchDarkly flag mechanism: flags are declared as schemas in
 * EPS and toggled per environment at GLOBAL scope, the same way backend workers
 * read them via `isGlobalFlagEnabled`.
 *
 * Reads `/configs/global-flags/:key`, which resolves the value server-side and
 * is open to client admins as well as platform admins. The admin endpoint at
 * `/admin/configs/global` is not used here: it is platform-admin-only, so a
 * client admin would silently fall through to `false`.
 *
 * Fail-safe: resolves to FALSE while loading and on any request error, so
 * callers get the pre-flag behaviour rather than a flash of the new surface.
 */
export default function useGlobalFlag(key: string): {
  isEnabled: boolean
  isLoading: boolean
} {
  const { data, isLoading } = useQuery({
    queryKey: ['globalFlag', key],
    queryFn: async (): Promise<boolean> => {
      try {
        const response = await epsClient.get<GlobalFeatureFlagResponse>(
          `/configs/global-flags/${key}`
        )
        return response.data?.isEnabled === true
      } catch {
        return false
      }
    },
    enabled: key.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  // `data === undefined` rather than react-query's `isLoading`, which is false
  // for a disabled query. Callers use this to hold rendering until the flag is
  // known, so "not yet answered" has to include the disabled case.
  return {
    isEnabled: data === true,
    isLoading: isLoading || data === undefined,
  }
}
